import { useRef, useState, useEffect } from 'react'
import { formatBalanceMeta } from '@/lib/format-number'
import { useAppSelector } from '@/store/hooks'
import { useGetBalancesQuery } from '@/store/api/paymentApi'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { selectDefaultBalance } from '@/store/slices/paymentSlice'
import { useBalanceUpdateSocket } from '@/store/socket/balance-socket'
import type { BalanceUpdateSocketData } from '@/types/payment'

/** Header wallet — animates only on socket `balanceUpdate`. */
export function useHeaderWallet() {
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	useGetBalancesQuery(undefined, { skip: !isAuthenticated })
	const balance = useAppSelector(selectDefaultBalance)
	const currencyCode = balance?.currency_code ?? ''
	const meta = formatBalanceMeta(balance?.balance)

	const [value, setValue] = useState(meta.value)
	const [from, setFrom] = useState(meta.value)
	const [animate, setAnimate] = useState(false)
	const [animationKey, setAnimationKey] = useState(0)
	const valueRef = useRef(meta.value)
	const currencyRef = useRef('')

	useEffect(() => {
		if (!currencyCode || currencyRef.current === currencyCode) return
		currencyRef.current = currencyCode
		valueRef.current = meta.value
		setValue(meta.value)
		setFrom(meta.value)
		setAnimate(false)
	}, [currencyCode, meta.value])

	useBalanceUpdateSocket((data: BalanceUpdateSocketData) => {
		if (data.currency_code.toUpperCase() !== currencyRef.current.toUpperCase()) return

		setFrom(valueRef.current)
		valueRef.current = data.balance
		setValue(data.balance)
		setAnimate(true)
		setAnimationKey((k) => k + 1)
	})

	return {
		value,
		from,
		animate,
		animationKey,
		decimalPlaces: meta.decimalPlaces,
		currencyCode,
		onAnimationComplete: () => setAnimate(false),
	}
}
